/**
 * RPC Provider Configuration Factory (Node.js Backend Version)
 * Single source of truth for RPC provider configurations
 * Reads from environment variables to eliminate hardcoded provider lists
 */

import logger from './utils/logger.js';

/**
 * Get provider type from environment variable name
 * Uses new naming convention: NODE, DEV, MEV
 */
function getProviderTypeFromName(envVarName) {
    if (envVarName.includes('_NODE_')) return 'node';
    if (envVarName.includes('_DEV_')) return 'dev';
    if (envVarName.includes('_MEV_')) return 'mev';
    return 'unknown';
}

/**
 * Get provider name from environment variable name using pure parsing
 * No hardcoded mappings - extracts from standardized naming structure
 */
function getProviderNameFromEnvVar(envVarName) {
    const parts = envVarName.split('_');


    if (envVarName.includes('_MEV_')) {
        // VITE_BASE_MEV_[NAME]_RPC_URL -> NAME
        const mevIndex = parts.indexOf('MEV');
        const providerName = parts[mevIndex + 1] || 'Unknown-MEV';

        // Enhanced debugging for MEV providers
        if (envVarName.includes('MEVBLOCKER') || envVarName.includes('DRPC')) {
            console.log(`🔍 MEV NAME PARSING: mevIndex=${mevIndex}, nextPart=${parts[mevIndex + 1]}, result=${providerName}`);
        }

        return providerName;
    }

    if (envVarName.includes('_NODE_')) {
        // VITE_ALCHEMY_BASE_NODE_MAINNET_URL -> ALCHEMY
        if (parts[0] === 'VITE' && parts.length > 1) {
            return parts[1] || 'Unknown-Node';
        }
        // [NAME]_BASE_NODE_MAINNET_URL -> NAME (for non-VITE prefixed)
        return parts[0] || 'Unknown-Node';
    }

    if (envVarName.includes('_DEV_')) {
        // VITE_DEV_RPC_URL -> Dynamic name based on URL
        // Check if it's ngrok or local
        const isRunningInBrowser = isBrowserEnvironment();
        let devUrl;

        if (isRunningInBrowser) {
            devUrl = import.meta.env.VITE_DEV_RPC_URL;
        } else {
            devUrl = process.env.VITE_DEV_RPC_URL || process.env.DEV_RPC_URL;
        }

        if (devUrl && devUrl.includes('ngrok')) {
            return 'Anvil-Ngrok';
        }
        return 'Anvil-Local';
    }

    // Fallback: try to find provider name in parts
    const excludeWords = ['VITE', 'BASE', 'NODE', 'DEV', 'MEV', 'RPC', 'URL', 'MAINNET'];
    const providerName = parts.find(part => !excludeWords.includes(part));
    return providerName || 'Unknown';
}

// Cache environment detection to avoid repeated logging
let _environmentCache = null;

/**
 * Detect if we're running in browser or Node.js environment
 * More robust detection for Vercel/production environments
 */
function isBrowserEnvironment() {
    // Return cached result if available
    if (_environmentCache !== null) {
        return _environmentCache;
    }

    // Check for browser-specific globals
    const hasBrowserGlobals = typeof window !== 'undefined' && typeof document !== 'undefined';

    // Check for import.meta (Vite/ES modules in browser)
    const hasImportMeta = typeof import.meta !== 'undefined' && import.meta && import.meta.env;

    // Check if we're NOT in Node.js (no require, no __dirname, etc.)
    const notNodeJs = typeof require === 'undefined' && typeof __dirname === 'undefined';

    const isBrowser = hasBrowserGlobals || (hasImportMeta && notNodeJs);

    // Cache the result and log only once
    _environmentCache = isBrowser;

    // Only log environment detection once
    console.log('🔍 Environment Detection (cached):', {
        hasBrowserGlobals,
        hasImportMeta,
        notNodeJs,
        isBrowser,
        hasProcess: typeof process !== 'undefined',
        hasWindow: typeof window !== 'undefined'
    });

    return isBrowser;
}

/**
 * Get environment variable with browser/Node.js compatibility
 * CRITICAL FIX: Strip shell script artifacts like '-n ' prefix from Vercel upload
 */
function getEnvVar(name) {
    const isRunningInBrowser = isBrowserEnvironment();

    if (isRunningInBrowser) {
        // In browser, only VITE_ prefixed variables are available via import.meta.env
        let value;

        if (name.startsWith('VITE_')) {
            value = import.meta.env[name];
        } else {
            value = import.meta.env[`VITE_${name}`];
        }

        // Strip shell script artifacts and trim whitespace
        return value ? value.trim().replace(/^-n\s+/, '').replace(/^-e\s+/, '') : value;
    } else {
        // In Node.js, try both versions (non-VITE first for backend compatibility)
        const value = process.env[name] || process.env[`VITE_${name}`];

        // CRITICAL FIX: Strip shell script artifacts like '-n ' or '-e ' prefix
        // These get accidentally included when using echo -n in deployment scripts
        return value ? value.trim().replace(/^-n\s+/, '').replace(/^-e\s+/, '') : value;
    }
}

/**
 * Create provider configuration from environment variables
 * Uses dynamic type detection from variable names (NODE/DEV/MEV)
 */
function createProviderConfig() {
    const isRunningInBrowser = isBrowserEnvironment();

    // Enhanced environment variable debugging - show both VITE and non-VITE versions
    const envDebug = {
        context: isRunningInBrowser ? 'browser' : 'node',
        CHAIN_ID: getEnvVar('CHAIN_ID'),
        FORK_ENABLED: getEnvVar('FORK_ENABLED'),
        ENVIRONMENT: getEnvVar('ENVIRONMENT'),
        DEV_RPC_URL: getEnvVar('DEV_RPC_URL'),
        NODE_ENV: isRunningInBrowser ? 'browser' : (process.env.NODE_ENV || 'unknown'),
        VERCEL: isRunningInBrowser ? 'browser' : (process.env.VERCEL || 'undefined'),
        VERCEL_ENV: isRunningInBrowser ? 'browser' : (process.env.VERCEL_ENV || 'undefined')
    };

    console.log('🏭 RPCProviderFactory: Environment Debug', envDebug);


    // Use environment-aware variable access
    const chainId = String(getEnvVar('CHAIN_ID') || '8453');
    const forkEnabled = getEnvVar('FORK_ENABLED') === 'true';
    const isLocalChain = chainId === '31337' || forkEnabled;
    const environment = getEnvVar('ENVIRONMENT') || 'production';

    console.log('🏭 RPCProviderFactory: Creating provider configuration', {
        chainId,
        isLocalChain,
        forkEnabled,
        environment,
        context: isRunningInBrowser ? 'browser' : 'node',
        isVercel: isRunningInBrowser ? false : !!process.env.VERCEL,
        vercelEnv: isRunningInBrowser ? 'browser' : process.env.VERCEL_ENV
    });

    if (isLocalChain) {
        // Local development: Use DEV provider with fallback logic
        const devUrl = getEnvVar('DEV_RPC_URL') || 'http://localhost:8545';
        const envType = forkEnabled ? 'fork' : 'local';

        return [{
            name: devUrl.includes('ngrok') ? 'Anvil-Ngrok' : 'Anvil-Local',
            url: devUrl,
            priority: 0,
            type: 'dev',
            requiresAuth: false,
            environment: envType,
            timeout: 10000
        }];
    }

    // Production: Dynamically build provider list from environment variables
    const providers = [];
    let priority = 1;

    // Get all environment variables (browser vs Node.js compatible)
    const allEnvVars = isRunningInBrowser ? import.meta.env : process.env;

    // Scan all environment variables using pattern-based detection
    for (const [key, value] of Object.entries(allEnvVars)) {
        if (!value) continue;

        // Use the proper function instead of inline pattern matching
        const providerType = getProviderTypeFromName(key);

        if (providerType === 'unknown') {
            // Skip non-RPC environment variables (Auth0, configs, etc.) - no logging needed
            continue;
        }

        const providerName = getProviderNameFromEnvVar(key);

        const isNodeProvider = providerType === 'node';
        const isMevProvider = providerType === 'mev';

        logger.debug(`🔍 Found ${providerType} provider: ${providerName} (${key})`);

        // For backend, NODE providers use direct URLs (not proxy)
        const newProvider = {
            name: providerName,
            url: value,
            priority: priority++,
            type: providerType,
            requiresAuth: isNodeProvider,
            environment: 'production',
            timeout: isMevProvider ? 6000 : 5000,
            mevProtection: isMevProvider
        };

        logger.debug(`🎯 ADDING PROVIDER: ${providerName} (${providerType}) to providers array`);
        providers.push(newProvider);
    }

    // Add Base Official as hardcoded fallback (not monitored, always available)
    providers.push({
        name: 'Base-Official',
        url: 'https://mainnet.base.org',
        priority: 999, // Lowest priority - used as fallback only
        type: 'fallback',
        requiresAuth: false,
        environment: 'production',
        timeout: 10000,
        fallback: true
    });

    // Sort by priority and return
    const sortedProviders = providers.sort((a, b) => a.priority - b.priority);

    console.log('🏭 Backend RPCProviderFactory: Created configuration', {
        totalProviders: sortedProviders.length,
        nodeProviders: sortedProviders.filter(p => p.type === 'node').length,
        devProviders: sortedProviders.filter(p => p.type === 'dev').length,
        mevProviders: sortedProviders.filter(p => p.type === 'mev').length
    });

    // Final comprehensive debug log
    console.log('🏭 FINAL SORTED PROVIDERS LIST:');
    sortedProviders.forEach((provider, index) => {
        console.log(`  ${index}: ${provider.name} (${provider.type}) - Priority: ${provider.priority}`);
    });

    return sortedProviders;
}

/**
 * Create provider configuration for health monitoring
 * Uses same dynamic parsing as main config but includes all providers for monitoring
 */
function createHealthMonitoringConfig() {
    // Use environment-aware variable access (same as main config)
    const chainId = String(getEnvVar('CHAIN_ID') || '8453');
    const forkEnabled = getEnvVar('FORK_ENABLED') === 'true';
    const isLocalChain = chainId === '31337' || forkEnabled;

    if (isLocalChain) {
        // In local/fork development, monitor ALL providers for code fidelity
        // This includes the Anvil provider (for transactions) + all production providers (for monitoring)
        const productionProviders = createProductionProvidersForMonitoring();

        const devUrl = getEnvVar('DEV_RPC_URL') || 'http://localhost:8545';
        const anvilProvider = {
            name: devUrl.includes('ngrok') ? 'Anvil-Ngrok' : 'Anvil-Local',
            url: devUrl,
            priority: 0,
            type: 'dev',
            requiresAuth: false,
            environment: forkEnabled ? 'fork' : 'local',
            timeout: 10000
        };

        // Silent health monitoring setup - no logging needed
        return [anvilProvider, ...productionProviders];
    }

    // Production: use regular provider configuration
    return createProviderConfig();
}

/**
 * Create production provider configuration for monitoring purposes using dynamic parsing
 * Used when in local development but need to monitor production providers
 */
function createProductionProvidersForMonitoring() {
    const providers = [];
    let priority = 1;
    const isRunningInBrowser = isBrowserEnvironment();

    // Get all environment variables (browser vs Node.js compatible)
    const allEnvVars = isRunningInBrowser ? import.meta.env : process.env;

    // Scan all environment variables using same pattern-based detection as main config
    for (const [key, value] of Object.entries(allEnvVars)) {
        // Silent MEV provider tracking - no logging needed

        if (!value) {
            if (key.includes('MEV_ALCHEMY') || key.includes('MEV_MAINNET')) {
                console.log(`❌ MEV PROVIDER SKIPPED: ${key} - NO VALUE`);
            }
            continue;
        }

        // Use the proper function instead of inline pattern matching
        const providerType = getProviderTypeFromName(key);

        if (providerType === 'unknown' || providerType === 'dev') {
            continue; // Skip unknown and dev providers for production monitoring
        }

        const providerName = getProviderNameFromEnvVar(key);
        const isNodeProvider = providerType === 'node';
        const isMevProvider = providerType === 'mev';

        // Silent provider detection - no logging needed for production monitoring

        providers.push({
            name: providerName,
            url: value,
            priority: priority++,
            type: providerType,
            requiresAuth: isNodeProvider,
            environment: 'production',
            timeout: isMevProvider ? 6000 : 5000,
            mevProtection: isMevProvider
        });
    }

    // Base Official is now loaded from environment variables like other providers
    // No hardcoded providers - all come from environment scanning

    console.log('🏭 Production Monitoring: Created', {
        totalProviders: providers.length,
        nodeProviders: providers.filter(p => p.type === 'node').length,
        mevProviders: providers.filter(p => p.type === 'mev').length
    });

    // Silent provider list creation - no debugging needed

    return providers.sort((a, b) => a.priority - b.priority);
}

/**
 * Get providers by type
 */
function getProvidersByType(type) {
    return createProviderConfig().filter(provider => provider.type === type);
}

/**
 * Get MEV protection providers only
 */
function getMEVProviders() {
    // In local/fork development, use health monitoring config to show all MEV providers
    // In production, use regular provider config
    const chainId = String(getEnvVar('CHAIN_ID') || '8453');
    const forkEnabled = getEnvVar('FORK_ENABLED') === 'true';
    const isLocalChain = chainId === '31337' || forkEnabled;

    if (isLocalChain) {
        // Use health monitoring config to get all MEV providers for display
        return createHealthMonitoringConfig().filter(provider => provider.type === 'mev');
    } else {
        // Use regular config in production
        return getProvidersByType('mev');
    }
}

/**
 * Get node RPC providers only (formerly "standard")
 */
function getNodeProviders() {
    return getProvidersByType('node');
}

/**
 * Get development providers only
 */
function getDevProviders() {
    return getProvidersByType('dev');
}

/**
 * @deprecated Use getNodeProviders() instead
 * Get standard RPC providers only (legacy compatibility)
 */
function getStandardProviders() {
    return getNodeProviders();
}

/**
 * Validate provider configuration
 */
function validateProviderConfig(providers) {
    if (providers.length === 0) {
        console.error('🚨 Backend RPCProviderFactory: No providers configured');
        return false;
    }

    const hasValidUrls = providers.every(provider => provider.url && provider.url.length > 0);
    if (!hasValidUrls) {
        console.error('🚨 Backend RPCProviderFactory: Some providers have invalid URLs');
        return false;
    }

    const priorities = providers.map(p => p.priority);
    const uniquePriorities = new Set(priorities);
    if (priorities.length !== uniquePriorities.size) {
        console.warn('⚠️ Backend RPCProviderFactory: Duplicate priorities detected');
    }

    console.log('✅ Backend RPCProviderFactory: Provider configuration validated');
    return true;
}

export {
    createProviderConfig,
    createHealthMonitoringConfig,
    getProvidersByType,
    getMEVProviders,
    getNodeProviders,
    getStandardProviders,
    validateProviderConfig
};
